import {
  Box,
  useFieldContext,
  type HTMLChakraProps,
  type SystemStyleObject
} from "@chakra-ui/react";

import { stripDangerousHtml } from "@/lib";

// v3's preflight sets `ol, ul { list-style: none }` and `* { font: inherit }`, the
// latter a shorthand that also flattens font-style. v2's reset did neither, so authored
// HTML leaned on user-agent defaults for markers and italics. Reverting these globally
// would put bullets back on menu/tab/select internals, so scope it to sanitized content.
const richTextDefaults: SystemStyleObject = {
  "& ul, & ol": { listStyle: "revert" },
  "& em, & i": { fontStyle: "italic" }
};

/**
 * Default wrapper to sanitize and render html elements
 * @param html string
 * @returns JSX.Element
 */

export interface HTMLWrapperProps extends HTMLChakraProps<"div"> {
  html: string;
  isFullWidth?: boolean;
}

function HTMLWrapper(props: HTMLWrapperProps) {
  const { html, className, isFullWidth, css, ...rest } = props;
  const { required } = useFieldContext() ?? {};
  const styles = css;

  return (
    <Box
      as={required ? "span" : "div"}
      className={["fr-view", className].filter(Boolean).join(" ")}
      css={[
        richTextDefaults,
        required
          ? {
              "& > :last-child": { display: "inline" },
              "& span.float-left": { float: "left" },
              ...styles
            }
          : { "& span.float-left": { float: "left" }, ...styles }
      ]}
      width={isFullWidth && !required ? "100%" : "auto"}
      dangerouslySetInnerHTML={{ __html: stripDangerousHtml(html) }}
      {...rest}
    />
  );
}

function HTMLWrapperWithoutOverrides(props: HTMLWrapperProps) {
  const { html, css, ...rest } = props;

  return (
    <Box
      css={[richTextDefaults, css]}
      dangerouslySetInnerHTML={{ __html: stripDangerousHtml(html) }}
      {...rest}
    />
  );
}

export { HTMLWrapper, HTMLWrapperWithoutOverrides };
